import { useEffect, useState } from 'react';
import { observable, flow, toJS, when } from 'mobx';
import _get from 'lodash.get';

import { addProfileDataToDocumentSnapshot } from '@ratehub/documents/utilities';

import timeoutAsPromise from '../functions/timeoutAsPromise';
import navigateWindowTo from '../functions/navigateWindowTo';
import noticeError from '../functions/noticeError';
import trackHeapEvent from '../functions/trackHeapEvent';
import fetchSession from '../functions/fetchSession';
import { authenticateMagicToken } from '../functions/authenticateMagicToken';
import { verifyAccount } from '../functions/verifyAccount';
import deauthenticateUser from '../functions/deauthenticateUser';
import signInBySocialService from '../functions/signInBySocialService';
import createDocumentDAO from '../functions/createDocumentDao';
import pushProfileUpdate from '../functions/pushProfileUpdate';
import areFirstPartyCookiesEnabled from '../functions/areFirstPartyCookiesEnabled';
import getDocumentDefinitionByType from '../functions/getDocumentDefinitionByType';
import fetchShouldForceLogin from '../functions/fetchShouldForceLogin';
import fetchUserCity from '../functions/fetchUserCity';
import getVisitorId from '../functions/getVisitorId';
import Experiments from '../definitions/ExperimentsObservable';
import sendLog from '../functions/sendLog';
import DOCUMENTS from '../definitions/Documents';
import UNIVERSAL_ERROR_MESSAGES from '../definitions/UniversalErrorMessages';
import SOCIAL_PROVIDERS from '../definitions/SocialProviders';
import { MessageBannerVariants } from '../definitions/MessageBannerDefinitions';
import PROFILE_TYPES from '../definitions/ProfileTypes';
import Config from '../definitions/Config';
import pushDocumentEvent from '../functions/pushDocumentEvent';
import fetchDocumentEvents from '../functions/fetchDocumentEvents';
import isNetworkError from '../functions/isNetworkError';
import useMessageBannerContext from './useMessageBannerContext';


const PROFILE_DEFAULTS = {
    type: null,

    id: null,
    uid: null,
    visitorId: null,

    email: null,
    phone: null,

    firstName: null,
    lastName: null,

    dob: null,
};


/**
 * Create a new instance of our Session Store, which provide an interface to our Profile Service.
 * @param {object} root0 - Options
 * @param {boolean} root0.shouldFetchOnLoad - If the session should be fetched on load
 * @returns {object} State
 */
function useSessionStoreState({ shouldFetchOnLoad }) {
    // We use this to signal something is wrong to the user (at the page level).
    // NOTE: May not be declared (Storybook, tests, etc).
    const messageBannerState = useMessageBannerContext();

    const [ state ] = useState(() => observable({
        // Public properties
        profile: { ...PROFILE_DEFAULTS },

        // Cookie status
        hasFirstPartyCookiesEnabled: areFirstPartyCookiesEnabled(),

        // Private, will hold document DAOs.
        _documents: {},
        // Private, read-only
        _roles: [],
        _userLocation: null, // we only fetch this if interestLocation is not available in their profile

        // API-related state (fetch session)
        isFetchingSession: false,
        fetchingSessionError: null,
        hasFetchedSession: false,
        // API-related state (push profile update)
        updateProfileError: null,
        // API-related state (should force login)
        isFetchingShouldForceLogin: false,
        fetchingShouldForceLoginError: null,

        isLoggingOut: false,
        hasLoggedOut: false,

        get _profileType() {
            if (state.profile.type != null) {
                return state.profile.type;
            }
            return state.profile.uid != null
                ? PROFILE_TYPES.VERIFIED
                : PROFILE_TYPES.GUEST;
        },
        get profileId() {
            return state._profileType === PROFILE_TYPES.VERIFIED
                ? state.profile.uid
                : state.profile.id;
        },
        get isLoggedIn() {
            return state._profileType === PROFILE_TYPES.VERIFIED;
        },
        get email() {
            return state.profile?.email;
        },
        get shortName() {
            if (state.profile.firstName && state.profile.lastName) {
                return `${state.profile.firstName.substring(0, 1)}${state.profile.lastName.substring(0, 1)}`;
            }
            return null;
        },

        get roles() {
            return state._roles ?? [];
        },

        get interestLocation() {
            return state.profile?.interestLocation ?? state._userLocation;
        },
        setInterestLocation(interestLocation) {
            state.pushProfileUpdate({ interestLocation });
        },

        /**
         * Check if we have the specified role.
         * @param {string} role - Role type
         * @returns {boolean} if the role is included
         */
        hasRole(role) {
            return state.roles.includes(role);
        },

        /**
         * Check if we have any of the roles in the array.
         *
         * This is useful for situations where multiple roles can access the same system, but we don't require a user to have all of them.
         * @param {Array<string>} roles - List of roles
         * @returns {boolean} if the role is included
         */
        hasOneOfRoles(roles) {
            return roles.some(state.hasRole);
        },

        /**
         * Update our contents by retrieving information from the Profile service.
         * @param {boolean} forced - if we should re-fetch even if already fetched
         * @returns {Promise<boolean>}
         */
        fetchSession: flow(function* (forced = false) {
            if (typeof window === 'undefined') {
                return false; // don't fetch on SSR
            }

            // Abort if already fetched
            if (state.hasFetchedSession && !forced) {
                return state.fetchingSessionError === null;
            }

            // Ignore request to fetch if we're already fetching
            if (state.isFetchingSession) {
                yield when(() => state.isFetchingSession === false);
                return state.fetchingSessionError === null;
            }

            state.isFetchingSession = true;
            state.fetchingSessionError = null;
            state.setUniversalErrorMessage(null);

            try {
                const { profile, roles } = yield fetchSession();

                // TODO: Remove this when backend for mortgages unidoc is cleaned up.
                // the id for application vs journey is the same which will cause conflicts
                // when trying to find a document by id.
                if (profile.documents?.mortgages) {
                    delete profile.documents.mortgages.application;
                }

                // location(base-ui's CityField object) could have been saved before Profile schema established
                if (profile.location) {
                    if (!profile.homeAddress) {
                        profile.homeAddress = {
                            city: profile.location.name,
                            province: profile.location.province?.code,
                        };
                    }
                    delete profile.location;
                }

                // Provide the fallback (user location) for interestLocation if it's not available in their profile
                if (!profile.interestLocation) {
                    state._userLocation = yield fetchUserLocation();
                }

                // Addresses XSS Vulnerability in a temporary way.
                // See: https://ratehub.atlassian.net/browse/PRO-642
                // This gets rendered within the profile drop-down ("Hi, <name>") using renderMessage (dangerouslySetInnerHTML)
                // WORKAROUND: filter out any open/close scripting tags and/or "
                if (profile.firstName) {
                    profile.firstName = profile.firstName.replace(/[<>"]/g, '');
                }
                if (profile.lastName) {
                    profile.lastName = profile.lastName.replace(/[<>"]/g, '');
                }

                state.profile = profile;
                state._roles = roles;

                // Build/update our DAOs
                state._documents = createOrUpdateDocuments(profile.documents, state);

                // Identify the user in hotjar once we've fetched the session
                //  We're doing this within fetchSession since we'd like
                //  to keep track of whether the user is logged in or not.
                identifyHotjarUser(state);

                // We don't currently support accessing the documents like this
                delete state.profile.documents;

                if (state.isLoggedIn) {
                    identifyLoggedInUsers();
                }
            } catch (error) {
                state.setUniversalErrorMessage(error);
                state.fetchingSessionError = error;

                // eslint-disable-next-line no-console
                console.error(`[SessionStore] Error loading profile: ${error.message}`, error);
            }

            state.hasFetchedSession = true;
            state.isFetchingSession = false;

            return state.fetchingSessionError === null;
        }),

        /**
         * Helper function
         * @param {string} path - Path name
         * @returns {Array<object>} document stubs
         */
        getDocumentMetadataByPath(path) {
            return Object.values(state._documents)
                .filter(document => document.path === path)
                .map(document => document.metadata);
        },
        getDocumentType(id) {
            return state._documents[id]?.documentType;
        },
        getDocumentPersistenceError(id) {
            return state._documents[id]?.persistenceError;
        },
        isDocumentLoading(id) {
            return state._documents[id]?.isLoading;
        },
        isDocumentSaving(id) {
            return state._documents[id]?.isSaving;
        },
        isDocumentDeleting(id) {
            return state._documents[id]?.isDeleting;
        },
        isDocumentPersisted(id) {
            return state._documents[id]?.isPersisted;
        },
        hasDocumentUnsavedChanges(id) {
            return state._documents[id]?.hasUnsavedChanges;
        },
        hasDocumentPersistenceError(id) {
            return state._documents[id]?.hasPersistenceError;
        },
        hasDocumentFetchError(id) {
            return state._documents[id]?.hasFetchError;
        },
        hasDocumentDeleteError(id) {
            return state._documents[id]?.hasDeleteError;
        },
        /**
         * Check if user needs to login based on their document.
         * Note: Please save document changes before running this function.
         * @param {string} documentId
         * @param {object} options
         * @param {string} options.documentRedirect
         * @param {object} options.queryParams
         */
        shouldForceLogin: flow(function* (documentId, { documentRedirect, queryParams } = {}) {
            const documentDao = state._documents[documentId];

            if (!documentDao) {
                throw new Error(`[SessionStore.shouldForceLogin] document with id "${documentId}" does not exist.`);
            }

            if (state.isLoggedIn) {
                return false; // nothing to do, they're already logged in.
            }

            // API depends on document information (ie. email), but we can't make assumptions about how they are saving the document.
            // This puts the onus on the user to flush any document changes before calling this API.
            if (documentDao.hasUnsavedChanges) {
                throw new Error(
                    '[SessionStore.shouldForceLogin] Please save any pending document changes before calling shouldForceLogin.',
                );
            }

            state.isFetchingShouldForceLogin = true;
            state.fetchingShouldForceLoginError = null;

            try {
                return yield fetchShouldForceLogin({
                    documentId,
                    documentType: documentDao.documentType,
                    documentRedirect,
                    queryParams,
                });
            } catch (error) {
                state.fetchingShouldForceLoginError = error;

                // eslint-disable-next-line no-console
                console.error(
                    `[SessionStore.shouldForceLogin] Error fetching should force login: ${error.message}`,
                    error,
                );

                // We should not block the user from progressing if there was an error.
                return false;
            } finally {
                state.isFetchingShouldForceLogin = false;
            }
        }),
        /**
         * Fetch a document by its ID, optionally using a factory on the raw document payload.
         * @param {string} id
         * @param {(snapshot: any) => any}
         */
        fetchDocumentById: flow(function* (id, factory) {
            // Ensure we have an up-to-date document list.
            yield state.fetchSession();
            if (state.fetchingSessionError) {
                throw new Error(
                    // eslint-disable-next-line @stylistic/max-len
                    `[SessionStore] Error during fetch session for document id: "${id}". You may need to re-fetch the session`,
                );
            }

            const documentDao = state._documents[id];

            if (!documentDao) {
                // Returns null instead of error because some BUs treat document not found as an expected behaviour
                // and shouldn't trigger error states
                return null;
            }

            yield documentDao.fetchDocument(factory);

            return documentDao.document;
        }),
        /**
         * Push document.
         * @deprecated please use startSavingDocumentChanges instead.
         * @param {object} document
         * @param {string} path
         */
        pushDocument: flow(function* (document, path) {
            try {
                if (!state._documents[document.id]) {
                    state._documents[document.id] = createDocumentDAO({
                        id: document.id,
                        path,
                        document,
                        sessionStore: state,
                    });
                }

                const documentDao = state._documents[document.id];

                yield documentDao.pushDocument(document);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('[SessionStore] pushDocument error : ', error);

                throw error;
            }

            state.isPushingDocument = false;
        }),
        /**
         * Fetch document events
         * @param {string} documentId
         * @param {Array} eventTypes - List of event type strings to filter
         */
        fetchEvents: flow(function*({ documentId, eventTypes = [] }) {
            try {
                const allEvents = yield fetchDocumentEvents({
                    documentId,
                    documentType: state.getDocumentType(documentId),
                });

                return eventTypes.length > 0
                    ? allEvents.filter(({ eventType }) => eventTypes.includes(eventType))
                    : allEvents;
            } catch (error) {
                noticeError(error, {
                    message: '[SessionStore] error while fetching events',
                    documentId,
                    eventTypes,
                });

                throw error;
            }
        }),

        /**
         * Push a document event.
         * @param {object} params
         * @param {string} params.documentId
         * @param {string} params.eventType
         */
        pushDocumentEvent: flow(function* ({ documentId, eventType, content }) {
            try {
                yield state.waitForDocumentToFinishSaving(documentId); // we don't want to push document events until the document is persisted

                yield pushDocumentEvent({
                    documentId,
                    documentType: state.getDocumentType(documentId),
                    eventType,
                    content,
                });
            } catch (error) {
                noticeError(error, {
                    message: '[SessionStore] pushDocumentEvent error',
                    documentId,
                    eventType,
                    // content is not included because it could contain PII
                });

                throw error;
            }
        }),
        // NOTE: Auto and Home applications can be deleted in dashboard
        // if you are going to use this function please make sure it's safe to use
        deleteDocument: flow(function* (id) {
            // delete doc in server first and then delete from sessionStore
            // assumption - server always has the doc if sessionStore has it
            try {
                if (!state._documents[id]) {
                    throw new Error(`No document with id "${id}" found in cached profile`);
                }

                yield state._documents[id].deleteDocument();
                delete state._documents[id];
            } catch (error) {
                noticeError(error, {
                    message: '[SessionStore] deleteDocument error',
                    documentId: id,
                });

                throw error;
            }
        }),
        /**
         * Update the profile details on the server.
         * If it succeeds, update our local representation with those changes.
         * @param {object} values
         */
        pushProfileUpdate: flow(function* (values) {
            state.setUniversalErrorMessage(null);
            state.updateProfileError = null;

            // Update our cache synchronously even if persistence fails.
            state._updateProfile(values);

            if (!state.hasFirstPartyCookiesEnabled) {
                return; // we can't call the API without cookies so don't bother
            }


            // ASSUMPTION: if it doesn't throw, it succeeded.
            try {
                yield pushProfileUpdate(values);
            } catch (error) {
                noticeError(error, {
                    message: '[SessionStore] pushProfileUpdate error',
                    profileId: state.profileId,
                    profileType: state._profileType,
                });

                state.setUniversalErrorMessage(error);

                state.updateProfileError = error;
            }
        }),
        /**
         * Authenticate.
         * @param {object} parameters
         * @param {string} parameters.email
         * @param {string} parameters.magicToken
         * @param {object} parameters.queryParams
         * @param {string} parameters.variant
         */
        authenticate: flow(function* ({ email, magicToken, queryParams = {}, documentOptions, variant } = {}) {
            yield authenticateMagicToken({ email, magicToken, queryParams, documentOptions, variant });
            state._documents = null;
            yield state.fetchSession(true); // re-fetch session after logging in
        }),
        /**
         * Sign up for new user
         * document is ignored here since document will be loaded in redirected page such as wizard
         * @param {object} parameters
         * @param {string} parameters.email
         * @param {string} parameters.magicToken
         * @param {object} parameters.queryParams
         * @param {string} parameters.variant
         * @returns {object} returns { profile, document }
         */
        verify: flow(function* ({ email, validationToken, queryParams = {}, documentOptions, variant } = {}) {
            yield verifyAccount({ email, validationToken, queryParams, documentOptions, variant });
            state._documents = null;
            yield state.fetchSession(true); // re-fetch session after signing up
        }),
        signInBySocialService: flow(function* ({
            providerToken,
            redirectTo,
            socialProvider = SOCIAL_PROVIDERS.GOOGLE,
            document = null,
            documentType = null,
        }) {
            try {
                trackHeapEvent('[GoogleSSOExperiment] Google-Sign-In requested', {
                    loginSource: socialProvider,
                });

                yield signInBySocialService({ providerToken, socialProvider, document, documentType });

                // TODO: remove this after Experiment is done
                trackHeapEvent('[GoogleSSOExperiment] Google-Sign-In verified', {
                    loginSource: socialProvider,
                });
                identifyLoggedInUsers(state.profileId);

                // TODO: redirectTo is required for now
                //   Refreshing/redirecting page is to prevent any wrong-doing of page
                //   while transitioning from Guest to Verified user
                navigateWindowTo(redirectTo);
            } catch (error) {
                noticeError(error, {
                    message: `[SessionStore] SSO sign in error: ${error.message}`,
                    socialProvider,
                    providerToken,
                });
            }
        }),
        /**
         * Logout.
         * @param {boolean} shouldRefetch - whether to re-fetch Session after clearing up cookies
         * When User get redirected to new page after logout, Session is to be re-fetched in new page
         * @returns {Promise<boolean>} isSuccessful
         */
        logout: flow(function* (shouldRefetch = true) {
            state.isLoggingOut = true;

            try {
                yield deauthenticateUser();

                state._documents = {};
                state.profile = { ...PROFILE_DEFAULTS };
                state._roles = [];
            } catch (error) {
                state.isLoggingOut = false;

                // eslint-disable-next-line no-console
                console.error(`[SessionStore] Error logging out: ${error.message}`, error);

                state.setUniversalErrorMessage(error);

                return false;
            }

            if (shouldRefetch) {
                try {
                    yield state.fetchSession(true); // re-fetch session after logging out
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error(
                        `[SessionStore] Error re-fetching session after logging out: ${error.message}`,
                        error,
                    );

                    state.setUniversalErrorMessage(error);
                    return false;
                }
            }

            state.hasLoggedOut = true;
            state.isLoggingOut = false;

            return true;
        }),
        setUniversalErrorMessage(error) {
            // SPECIAL CASE: clearing a message.
            if (error == null) {
                messageBannerState?.hide();
                return;
            }

            // Show the appropriate message.
            messageBannerState?.show({
                callerName: 'SessionStore',
                message: getMessageForError(error),
                variant: MessageBannerVariants.ERROR,
            });

            // Our services are unable to authenticate users with disabled cookies.
            // Do NOT set off alarms: there is nothing wrong with our system.
            if (state.hasFirstPartyCookiesEnabled || ![ 401, 403 ].includes(error.status)) {
                // TODO: @mccullough-ratehub - do not error if (profile BFF and status is 401 or 403 and hasFirstPartyCookiesEnabled disabled)
                noticeError(error, {
                    message: `[SessionStore]: ${error.message}`,

                    // Include profile information to cross-reference with BE
                    profileId: state.profileId,
                    profileType: state._profileType,
                });
            }
        },
        // Please do not use this function outside of SessionStore or DAO
        _updateProfile(profile) {
            if (typeof profile === 'object' && Object.keys(profile ?? {}).length > 0) {

                const { documents, ...updatedValues } = profile; // this is to exclude documents without mutating profile object

                state.profile = {
                    ...toJS(state.profile),
                    ...updatedValues,
                };
            }
        },
        /**
         * Note: This expects you to be using `startSavingDocumentChanges`; the use case is when you have to do something only after the document changes are saved (eg. make an API call).
         * @param {string} id - Document ID
         * @returns {Promise<void>} Resolves on success.
         */
        async waitForDocumentToFinishSaving(id) {
            if (!state._documents[id]) {
                throw new Error(
                    `No document with id "${id}" found in cached profile. You may need to re-fetch the session.`,
                );
            }

            // This will ensure that any queued microtasks which result in mutations to the model are allowed to run.
            await Promise.resolve();

            if (!state.hasDocumentUnsavedChanges(id)) {
                return; // no changes to wait for
            }

            // hasUnsavedChanges will be true if there's an error, so check if there's an error to resolve.
            await when(() => state.hasDocumentUnsavedChanges(id) === false || state.hasDocumentPersistenceError(id));

            if (state.hasDocumentPersistenceError(id)) {
                const error = state.getDocumentPersistenceError(id);

                sendLog(
                    `[SessionStore] error on waitForDocumentToFinishSaving: ${state.getDocumentType(id)} ${id}`,
                    { error: error
                        ? JSON.stringify(error.stack ?? error.message ?? error)
                        : 'N/A' }
                );

                // Network errors are not expected to be propagated as the error banner handles the UX for said errors
                if (!isNetworkError(error)) {
                    throw error;
                }
            }
        },
        addProfileDataToDocumentSnapshot(documentType, snapshot) {
            const schemaOptions = getDocumentDefinitionByType(documentType).SCHEMA_OPTIONS ?? null;
            return addProfileDataToDocumentSnapshot(snapshot, toJS(state.profile), schemaOptions);
        },
    }));

    // Prevent fetching profile on SSR.
    useEffect(
        () => {
            if (!shouldFetchOnLoad) {
                return;
            }

            state.fetchSession()
            // We need to catch exceptions here to prevent uncaught promise rejection warnings
            // We're already logging the error so no need to log it again here.
                .catch(() => {});
        },
        [ state, shouldFetchOnLoad ]
    );

    return state;
}

/**
 * Create a map of Data Access Objects (indexed by their .id) for all documents in a profile
 * @param {object} documents - map of document stubs to create from (index by their path)
 * @param {object} sessionStore - sessionStore caller which is being updated
 * @returns {object} new map of DAOs
 */
function createOrUpdateDocuments(documents, sessionStore) {
    // Only build DAOs for paths we recognize
    return Object.values(DOCUMENTS).map(doc => doc.PATH).reduce((result, path) => {
        // Build a DAO for each Document under this path
        const documentsInPath = Object.values(_get(documents, path) ?? {});
        documentsInPath.forEach(document => {
            // Preserve the existing DAO (if available) to prevent orphaned references
            let dao = Object.values(sessionStore._documents ?? {}).find(d => d.id === document.id);
            if (dao) {
                dao.metadata = document;
            } else {
                dao = createDocumentDAO({
                    id: document.id,
                    metadata: document,
                    path,
                    isPersisted: true,
                    sessionStore,
                });
            }

            result[document.id] = dao;
        });

        return result;
    }, {});
}

// `_userLocation` is a special API we provide, but it doesn't exist in the Profile.
async function fetchUserLocation() {
    try {
        const city = await Promise.race([
            fetchUserCity(),
            // Abort if its going to take too long.
            // We chose 10 seconds because of the amount of NR errors,
            //  we want to accommodate users with slow data.
            timeoutAsPromise(10000).then(() => {
                throw new Error('Timed out while fetching geo-location; Aborting!');
            }),
        ]);

        if (!city) {
            return null; // could not locate user
        }

        return {
            city: city.name,
            province: city.province.code,
            isFromGeoIP: true,
        };
    } catch (error) {

        noticeError(error, {
            message: '[SessionStore.getUserLocation] Error getting user location',
        });

        return null; // error fetching user location should not crash SessionStore
    }
}

/**
 * Get the i18n message to show to users when an error has occurred
 * @param {Error} error - Error instance
 * @returns {string} - Error message
 */
function getMessageForError(error) {
    // Certain kinds of Profile API Errors have different instructions.
    if (error.url?.match(`${Config.PROFILE_BFF_URL}`)) {
        if (error.status === 498) { // auth token error
            return UNIVERSAL_ERROR_MESSAGES.RECOVERABLE_BY_LOGOUT;
        } else if (
            error.status === 401 // cloudflare service unavailable
            || error.status === 504 // cloudflare gateway time out
            || error.status === 524 // cloudflare timeout
        ) {
            return UNIVERSAL_ERROR_MESSAGES.RECOVERABLE_BY_TRY_AGAIN;
        }
    }

    // Everything else falls under "hopefully refresh browser fixes it".
    return UNIVERSAL_ERROR_MESSAGES.RECOVERABLE_BY_REFRESH;
}

/* Adds unique attributes to hotjar so we can track
    more information about a user */
function identifyHotjarUser(sessionStore) {
    // window.hj has to exist for us to identify the user in hotjar
    if (typeof window?.hj === 'undefined') {
        return;
    }

    // Use visitorId as unique identifier
    const visitorId = getVisitorId();

    // Documentation for the identify API:
    // https://help.hotjar.com/hc/en-us/articles/360033640653#making-calls-to-identify
    window.hj('identify', visitorId, {
        isLoggedIn: sessionStore.isLoggedIn,

        // Hotjar only accepts strings, numbers, and bools
        //  we need to cast our experiments to a string so its available
        experiments: JSON.stringify(Experiments.toJSON()),
    });
}

/**
 * Memberships wants to track the number of logged in users;
 * Currently we can only track if they visit the dashboard- but they might not.
 */
function identifyLoggedInUsers() {
    trackHeapEvent('MBR - Sitewide - Logged-In-Users');
}

export default useSessionStoreState;
